/*
 * @Description: 用户接口
 * @Author: Mars
 * @Date: 2023-11-20 11:13:46
 */
import axios from 'axios';
import http from '@/utils/http';
import * as Types from './types';

export default {
  /**
   * @description: SSO 免登——凭 AlphaCloud 签发的一次性 ticket 向 users-center 兑换派生子会话
   * ticket 存于签发方数据中心，须直连跳转 URL 携带的 endPoint；不走本系统 request 实例
   * （baseURL / Authorization 注入 / 响应包格式均不同，且 ticket 兑换无需鉴权头）
   * @param {string} endPoint 数据中心接口地址（跳转 URL 的 endPoint 参数，形如 https://platform-au.alphaess.com/api）
   * @param {string} ticket 一次性 opaque ticket（60s TTL，单次消费）
   */
  async exchangeSsoTicket(endPoint: string, ticket: string) {
    // 显式超时：endPoint 网络层挂起时让兑换走失败分支，避免启动阶段的全屏 loading 悬死。
    // 取 30s——跨数据中心弱网要留足余量(客户端提前掐断可能误杀已被服务端消费的 ticket)，
    // 但不必到常规 50s：ticket 仅 60s TTL，等更久也无意义
    const { data } = await axios.post<Types.ISsoExchangeResp>(`${endPoint}/users-center/sessions/exchange`, { ticket }, { timeout: 30 * 1000 });
    return data;
  },

  /**
   * @Author: Mars
   * @description: 登录
   * @param {Types.ILoginParams} data 用户名密码
   * @return {*}
   */
  async login(data: Types.ILoginParams) {
    return await http.post<Types.ILoginResp>({ url: '/usercenter/cloud/user/login', data });
  },

  /**
   * @Author: Mars
   * @description: 退出登录
   * @return {*}
   */
  async logout() {
    return await http.post({ url: '/stable/account/logout' });
  },

  /**
   * @Author: Mars
   * @description: 获取用户信息
   * @return {*}
   */
  async getUserInfo() {
    return await http.get<Types.IUserInfo>({ url: '/stable/account/getUserInfo' });
  },

  /**
   * @Author: Wayne Hu
   * @description: 忘记密码发送邮件
   * @param {Types} data
   * @return {*}
   */
  async forgetPasswordSendEmail(data: Types.IForgetPasswordEmailParams) {
    return await http.post<Types.IForgetPasswordEmailRes>({ url: '/stable/user/forgetPassword', data });
  },

  /**
   * @Author: Wayne Hu
   * @description: 忘记密码，校验验证码
   * @param {Types} data
   * @return {*}
   */
  async forgetPasswordConfirmCode(data: Types.IForgetPasswordParams) {
    return await http.post<Types.IForgetPasswordRes>({ url: '/stable/user/confirmCodeForForgetPassword', data });
  },

  /**
   * @Author: Wayne Hu
   * @LastEditors: Wayne Hu
   * @description: 忘记密码，重置密码
   * @param {Types} data 接口参数
   * @return {*}
   */
  async forgetPasswordResetPassword(data: Types.IForgetPasswordResetParams) {
    return await http.post<object>({ url: '/stable/user/resetPassword', data });
  },

  /**
   * @Author: YuKi
   * @description: 检查refresh_token是否存在
   * @return {*}
   */
  async refreshToken(refresh_token: string) {
    return await http.get<boolean>({ url: '/oauth2/v1/check/refresh-token', params: { refresh_token } });
  },
  /**
   * @Author: YuKi
   * @description: 刷新token
   * @return {*}
   */
  async authorize(data: Types.IAuthorize) {
    return await http.post<Types.IAuthorizeResp>({ url: '/oauth2/v1/authorize', data });
  },

  /**
   * @Author: YuKi
   * @description: 验证token是否有效
   * @param {Types} data
   */
  async accessToken(access_token: string) {
    return await http.get<Types.IAccessRes>({ url: '/oauth2/v1/check/access-token', params: { access_token }, fullData: true });
  },
};
